import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie } from '@nivo/pie';
import { format } from 'date-fns';
import { Loader2, Clock, Users, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface TimeEntry {
  id: string;
  user_id: string;
  minutes_added: number;
  previous_total: number;
  new_total: number;
  source: string;
  notes: string | null;
  created_at: string;
  profiles?: {
    full_name: string | null;
    avatar_url: string | null;
  };
}

interface TimeHistoryTabProps {
  taskId: string;
  taskType: 'foundational' | 'growth' | 'recurring';
  brandId: string;
}

export const TimeHistoryTab = ({ taskId, taskType, brandId }: TimeHistoryTabProps) => {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchTimeHistory = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('os_universal_time_tracking')
          .select('*')
          .eq('task_id', taskId)
          .eq('task_type', taskType)
          .eq('entry_type', 'time_spent')
          .order('created_at', { ascending: true });

        if (error) throw error;
        setEntries(data || []);

        // Fetch user names for each unique user_id
        const userIds = [...new Set((data || []).map(e => e.user_id).filter(Boolean))];
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', userIds);
          
          const nameMap: Record<string, string> = {};
          profiles?.forEach(p => {
            nameMap[p.id] = p.full_name || 'Unknown User';
          });
          setUserNames(nameMap);
        }
      } catch (error) {
        console.error('Error fetching time history:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimeHistory();
  }, [taskId, taskType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Clock className="h-12 w-12 mb-2 opacity-50" />
        <p className="text-sm">No time entries recorded yet</p>
        <p className="text-xs">Time will be tracked when you save time updates</p>
      </div>
    );
  }

  // Prepare timeline data for line chart
  const lineData = [
    {
      id: 'Time Spent',
      data: entries.map((entry, index) => ({
        x: format(new Date(entry.created_at), 'MMM d, HH:mm'),
        y: entry.new_total,
      })),
    },
  ];

  // Prepare data for person analysis pie chart
  const personTimeMap: Record<string, number> = {};
  entries.forEach(entry => {
    const userId = entry.user_id || 'unknown';
    const userName = userNames[userId] || 'Unknown User';
    if (!personTimeMap[userName]) {
      personTimeMap[userName] = 0;
    }
    personTimeMap[userName] += Math.max(0, entry.minutes_added);
  });

  const pieData = Object.entries(personTimeMap)
    .filter(([_, value]) => value > 0)
    .map(([name, value]) => ({
      id: name,
      label: name,
      value,
    }));

  // Calculate stats
  const totalTime = entries.length > 0 ? entries[entries.length - 1].new_total : 0;
  const totalEntries = entries.length;
  const avgPerEntry = totalEntries > 0 ? Math.round(totalTime / totalEntries) : 0;

  const formatMinutes = (minutes: number) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes === 0 ? `${hours}h` : `${hours}h ${remainingMinutes}min`;
  };

  const sourceLabels: Record<string, string> = {
    quick_add_15: '+15 min',
    quick_add_30: '+30 min',
    quick_add_60: '+60 min',
    manual: 'Manual',
    adjustment: 'Adjustment',
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-muted/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <div>
                <p className="text-xs text-muted-foreground">Total Time</p>
                <p className="text-lg font-semibold">{formatMinutes(totalTime)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground">Entries</p>
                <p className="text-lg font-semibold">{totalEntries}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-muted/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground">Contributors</p>
                <p className="text-lg font-semibold">{Object.keys(personTimeMap).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timeline Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Time Progress Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveLine
              data={lineData}
              margin={{ top: 10, right: 20, bottom: 50, left: 50 }}
              xScale={{ type: 'point' }}
              yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -45,
                legend: '',
                legendOffset: 36,
                legendPosition: 'middle',
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Minutes',
                legendOffset: -40,
                legendPosition: 'middle',
              }}
              colors={['hsl(var(--primary))']}
              pointSize={8}
              pointColor={{ theme: 'background' }}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              enableArea={true}
              areaOpacity={0.1}
              useMesh={true}
              enableGridX={false}
              theme={{
                axis: {
                  ticks: {
                    text: {
                      fontSize: 10,
                      fill: 'hsl(var(--muted-foreground))',
                    },
                  },
                  legend: {
                    text: {
                      fontSize: 11,
                      fill: 'hsl(var(--muted-foreground))',
                    },
                  },
                },
                grid: {
                  line: {
                    stroke: 'hsl(var(--border))',
                    strokeOpacity: 0.5,
                  },
                },
                crosshair: {
                  line: {
                    stroke: 'hsl(var(--primary))',
                    strokeWidth: 1,
                  },
                },
              }}
              tooltip={({ point }) => (
                <div className="bg-background border border-border rounded-md shadow-lg px-3 py-2 text-sm">
                  <strong>{point.data.xFormatted}</strong>
                  <br />
                  Total: {formatMinutes(point.data.y as number)}
                </div>
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Person Analysis */}
      {pieData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Time by Person
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsivePie
                data={pieData}
                margin={{ top: 10, right: 80, bottom: 10, left: 80 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                activeOuterRadiusOffset={8}
                colors={{ scheme: 'nivo' }}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                arcLinkLabelsSkipAngle={10}
                arcLinkLabelsTextColor="hsl(var(--foreground))"
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: 'color' }}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                valueFormat={(value) => formatMinutes(value)}
                tooltip={({ datum }) => (
                  <div className="bg-background border border-border rounded-md shadow-lg px-3 py-2 text-sm">
                    <strong>{datum.label}</strong>
                    <br />
                    {formatMinutes(datum.value)}
                  </div>
                )}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Entries List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Recent Time Entries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {[...entries].reverse().slice(0, 10).map((entry) => (
              <div 
                key={entry.id} 
                className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0"
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center">
                    {(userNames[entry.user_id] || 'U').slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <span className="font-medium">{userNames[entry.user_id] || 'Unknown'}</span>
                    <span className="text-muted-foreground mx-2">•</span>
                    <span className="text-muted-foreground">
                      {sourceLabels[entry.source] || entry.source}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span className={entry.minutes_added >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {entry.minutes_added >= 0 ? '+' : ''}{entry.minutes_added}min
                  </span>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(entry.created_at), 'MMM d, HH:mm')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
